import {
  doc,
  collection,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

import { db, storage } from './firebaseConfig.js';
import { ResumeRepository } from '../repositories/ResumeRepository.js';
import { Resume } from '../../domain/models/Resume.js';

/**
 * Firebase/Firestore implementation of ResumeRepository.
 *
 * - 'resumes' collection: private, full resume data (auth-gated reads/writes)
 * - 'publicResumes' collection: managed exclusively by the syncPublicResume Cloud Function
 */
export class FirebaseResumeRepo extends ResumeRepository {

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------

  /** @returns {Promise<Resume>} */
  async create(resume) {
    const docRef = doc(collection(db, 'resumes'));
    const id = docRef.id;

    const resumeWithMeta = new Resume({
      ...resume,
      id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await setDoc(docRef, resumeWithMeta.toFirestore());

    // Return with the generated id; timestamps will be server-resolved
    return resumeWithMeta;
  }

  /** @returns {Promise<Resume|null>} */
  async getById(id) {
    const snap = await getDoc(doc(db, 'resumes', id));
    if (!snap.exists()) return null;
    return Resume.fromFirestore(snap.id, snap.data());
  }

  /** @returns {Promise<Resume|null>} */
  async getByUserId(userId) {
    const q = query(
      collection(db, 'resumes'),
      where('userId', '==', userId),
      limit(1),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const first = snapshot.docs[0];
    return Resume.fromFirestore(first.id, first.data());
  }

  /** @returns {Promise<void>} */
  async update(id, data) {
    const docRef = doc(db, 'resumes', id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  /** @returns {Promise<void>} */
  async delete(id) {
    // Read the resume first so we can clean up related assets
    const resume = await this.getById(id);

    // Delete private document
    await deleteDoc(doc(db, 'resumes', id));

    // Delete public mirror
    await deleteDoc(doc(db, 'publicResumes', id));

    // Delete OG image from Storage (if it exists)
    if (resume) {
      try {
        const ogRef = ref(storage, `og-images/${id}.png`);
        await deleteObject(ogRef);
      } catch (_) {
        // OG image may not exist — that's fine
      }
    }
  }

  /** @returns {Promise<Resume|null>} - Public resume data (sanitized) */
  async getPublicResume(id) {
    const snap = await getDoc(doc(db, 'publicResumes', id));
    if (!snap.exists()) return null;
    return Resume.fromFirestore(snap.id, snap.data());
  }

}
